
function lineRegex () {
    const separator    = "([\\(,@])"
    const fileName     = "(.*?)"
    const lineNumber   = "(\\d+)"
    const columnNumber = "(\\d+)"
    const optionalParenthesis = "(\\)?)"
    return new RegExp(`(.*)${separator}${fileName}:${lineNumber}:${columnNumber}${optionalParenthesis}`)
}


const regex = lineRegex()


// The three probes below throw a ReferenceError on purpose to measure the line
// offset the browser adds to eval'd code. They run on demand, the first time an
// offset is asked for (linesToRemove): a page that never evals user code never
// probes, and never trips a CSP without 'unsafe-eval'. Where the probe itself is
// blocked (EvalError, at construction time for the constructors) the offset falls
// back to 0: offsets only matter where user code can eval at all.

function probeEval () {
    try {
        eval("error()") //eslint-disable-line no-eval
    } catch (error) {
        return offsetFromProbeError(error)
    }
    return 0
}


function probeFunctionEval () {
    try {
        new Function("", "error()")()
    } catch (error) {
        return offsetFromProbeError(error)
    }
    return 0
}


const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor

// an async body runs synchronously until its first await: catching inside it
// hands the error back at once, no rejected promise to wait for
function probeAsyncFunctionEval () {
    const probe = {}
    try {
        new AsyncFunction("probe", "try { error() } catch (e) { probe.error = e }")(probe)
    } catch (error) {
        return 0
    }
    return probe.error ? offsetFromProbeError(probe.error) : 0
}


function offsetFromProbeError (error) {
    if (!(error instanceof ReferenceError)) {
        return 0
    }
    improveLineAndColumnError(error)
    return error.lineNumber - 1
}


const probes = new Map([
    ["eval",              probeEval],
    ["functionEval",      probeFunctionEval],
    ["asyncFunctionEval", probeAsyncFunctionEval]
])

const linesAddedByBrowser = new Map([["unknown", 0]])


function linesAddedFor (from) {
    if (!linesAddedByBrowser.has(from)) {
        const probe = probes.get(from)
        linesAddedByBrowser.set(from, probe ? probe() : 0)
    }
    return linesAddedByBrowser.get(from)
}


function errorModifier (error, params) {

    error.toxiTrace = error.toxiTrace || []
    error.toxiTrace.unshift("Toxilibs - error_modifier.js")

    error.localStackLines = error.localStackLines || []

    createDefaultMessage(error)
    createDefaultStack(error)

    improveLineAndColumnError(error, params)

    if (error.callbackTraces) {
        error.callbackTraces.forEach(trace => {
            trace.localStackLines = trace.localStackLines || []
            improveLineAndColumnError(trace, params)
        })
    }

}


function linesToRemove ({from = "unknown", ignoredLines = 0} = {}) {
    return ignoredLines + linesAddedFor(from)
}

errorModifier.linesToRemove = linesToRemove


//FIXME integrate better than putting it on the errorModifier function
errorModifier.addLastPositionToExecutionError = function (lastExecutionPoint, error) {
    if (error.hasKnownPosition) {
        return // an exact position was resolved (compilation error) : don't guess over it
    }
    if (lastExecutionPoint) {
        let {line, column, sourceName} = lastExecutionPoint
        error.lineNumber = line
        error.column     = column
        error.sourceName = sourceName
        error.originalMessage = error.message
        error.message = `in source "${sourceName
        }" line ${line}, ${
            error.message}`
    }
}


//FIXME integrate better than putting it on the errorModifier function
errorModifier.defineTraceableCallbacks = function (object, callbackNames, handleError) {
    object._traceableCallbacksData = object._traceableCallbacksData || {}
    object._traceableCallbacksData.errorHandler = handleError
    object._traceableCallbacksData.traces       = object._traceableCallbacksData.traces || {}
    callbackNames.forEach(callbackName => {
        defineTraceableCallback(object, callbackName)
    })
}

function defineTraceableCallback (object, callbackName) {
    let value
    Object.defineProperty(object, callbackName, {
        enumerable: true,
        get: () => value,
        set: (newValue) => {
            value = newValue
            object._traceableCallbacksData.traces[callbackName] = new Error(callbackName)
        }
    })
}


errorModifier.executeCallback = function (a, b, ...args) {
    if (typeof a === "function") {
        return executeCallback(a, b, ...args)
    } else {
        return executeCallbackForObject(a, b, ...args)
    }
}


function executeCallbackForObject (object, callbackName, ...args) {
    let action = object[callbackName]
    if (!action) {
        return null
    }
    return executeCallback(action, {
        handleError: object._traceableCallbacksData.errorHandler,
        trace:       object._traceableCallbacksData.traces[callbackName]
    }, ...args)
}


function executeCallback (callback, {handleError, trace, toxiTraceLabel}, ...args) {
    try {
        return callback(...args)
    } catch (error) {
        if (trace) {
            addCallbackTrace(error, trace)
        }

        error.toxiTrace = error.toxiTrace || []
        error.toxiTrace.unshift("Toxilibs - error_modifier.js - executeCallback")

        if (toxiTraceLabel) {
            error.toxiTrace.unshift(toxiTraceLabel)
        }

        if (handleError) {
            handleError(error)
            return null
        } else {
            throw error
        }
    }
}


function addCallbackTrace (error, trace) {
    error.callbackTraces = error.callbackTraces || []
    error.callbackTraces.push(trace)
    if (!errorModifier.noAutoAddCallbackTraces) {
        let lines = trace.stack.split("\n")
        error.stack += `\n${lines.join("\n")}`
    }
}



function createDefaultMessage (error) {
    if (typeof error.message === "undefined" || error.message === "") {
        error.message = "Undefined error"
    }
}


function createDefaultStack (error) {
    if (typeof error.stack === "undefined") {
        const {
            message,
            line         = 1,
            lineNumber   = line,
            columnNumber = 1,
            sourceURL    = "<anonymous>"
        } = error
        error.stack = `${message}\nat ${sourceURL}:${lineNumber}:${columnNumber}`
    }
}


function improveLineAndColumnError (error, params = {}) {
    let replacedCounter = 0

    let stackLines = error.stack.split("\n")

    error.stack = stackLines.map(processStackLine).join("\n")

    function processStackLine (stackLine, lineIndex) {
        return stackLine.replace(regex, function (fullMatch, firstPart, separator, sourceName, lineNumber, columnNumber, optionalParenthesis) { //eslint-disable-line max-params, complexity


            // hack for "eval" cases with lines like
            // at eval (eval at _toxilib_execute_for_anonymousEvaluator (anonymous_evaluator), <anonymous>:2:9)
            // this happens in execute_specs for instance
            if (sourceName.endsWith("<anonymous>")) {
                sourceName = firstPart.match(/([^(]*)\)$/)[1]
            }

            let p = paramsForSource(params, sourceName)

            if (dontModify({p, error, sourceName, lineIndex})) {
                return fullMatch
            }


            lineNumber   = parseInt(lineNumber, 10) - linesToRemove(p)
            columnNumber = parseInt(columnNumber, 10)


            if (p.findOriginalPosition) {
                let originalPosition = p.findOriginalPosition({line: lineNumber, column: columnNumber})
                if (!originalPosition) {
                    return fullMatch
                }
                lineNumber   = originalPosition.line
                columnNumber = originalPosition.column
                sourceName   = originalPosition.sourceName
            }


            if (replacedCounter === 0) {
                error.lineNumber   = lineNumber
                error.columnNumber = columnNumber
            }

            //FIXME executionId
            sourceName = sourceName.replace(/-run-\d+$/, "")


            if (error.localStackLines) {
                error.localStackLines.push({
                    stackLine: lineIndex,
                    sourceName,
                    firstPart: firstPart.trim(),
                    line:   lineNumber,
                    column: columnNumber
                })
            }

            replacedCounter += 1
            return `${firstPart}${separator}${sourceName}:${lineNumber}:${columnNumber}${optionalParenthesis}`
        })
    }

    if (error.localStackLines) {
        error.localStackLines.sort((a, b) => {
            return a.stackLine - b.stackLine
        })
    }
    
}


function dontModify ({p, error, lineIndex, sourceName}) {
    return !p ||
    (p.sourceName && p.sourceName !== sourceName) ||
    (error.localStackLines && error.localStackLines.find(l => l.stackLine === lineIndex))
}


function paramsForSource (params, sourceName) {
    if (params.paramsBySource) {
        return params.paramsBySource[sourceName] || false
    }
    return params
}



export default errorModifier

